import { faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { JSONContent } from "@tiptap/react";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import {
  addComment,
  addGuidesExamples,
  askToDelete,
  changeComment,
  changePossibleAnswerOrderNumber,
  findConditionToChange,
  setAnswerValue,
  setCurrentChapter,
  setQuestionValue,
} from "feature/producer/actions";
import { motion } from "framer-motion";
import debounce from "lodash.debounce";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Question } from "types/type.module";
import { GuidanceOrExample, TextElement } from "types/types";
import RichTextTranslator from "../../Logic/RichTextTranslator";
import {
  addPossibleAnswerToBackend,
  changePossibleAnswerToBackend,
  changeQuestionInBackend,
  removeExamples,
  removeFrontAnswers,
  removeGuidance,
  selectCurrentChapter,
  selectCurrentModule,
  selectProducerNewBeEvents,
  selectQuestionFromCurrentModuleByChapterAndQuestion,
} from "../../Redux/ProducerSlice";
import Button from "../Atoms/Button";
import ElementLabel from "../Atoms/ElementLabel";
import GuideButtons from "../Atoms/GuideButtons";
import QuestionButton from "../Atoms/QuestionButton";
import { CompleteEditor } from "../Molecule/MinimalEditor";
import StepCondition from "../Molecule/StepCondition";
import { selectStyle } from "Redux/appSlice";

/**
   @function QuestionProducer
   render Question for Porducer
   @param question {object} - question to render
   @return {Node}
*/

export default function QuestionProducer({
  question,
  index,
  onQuestionChange,
  onAnswerChange,
  onAddAnswer,
  onGuideExampleChange,
  createQuestionSnapshot,
}: {
  question: Question & { chapter_index: number; question_index: number };
  index: number;
  onQuestionChange: (
    index: number,
    val: JSONContent,
    question_id?: string
  ) => void;
  onAnswerChange: (
    val: JSONContent,
    possible_answer_id?: string,
    frontAnswerId?: string
  ) => void;
  onAddAnswer: (orderNumber: number) => void;
  onGuideExampleChange: (
    type: "guidances" | "examples",
    val: JSONContent,
    element: any,
    isTitle: boolean,
    id: string
  ) => void;
  createQuestionSnapshot: (question: Question) => any;
}) {
  const { color4 } = useAppSelector(selectStyle);
  const [showAddAnswer, setShowAddAnswer] = useState(false);
  const [sideTextVisibility, setSideTextVisibitlity] = useState(false);
  const [sideTextStatus, setSideTextStatus] = useState(
    generateSideTextFromAnswers(question.possible_answers)
  );
  const refreshedQuestion = useAppSelector((state) =>
    selectQuestionFromCurrentModuleByChapterAndQuestion(state, {
      chapterIndex: question.chapter_index,
      questionIndex: question.question_index,
    })
  );

  const [guide, setGuide] = useState<any>([["p", {}, "example"]]);
  const [example, setExample] = useState<any>([["p", {}, "example"]]);

  const currentModule = useAppSelector(selectCurrentModule);
  const currentChapter = useAppSelector(selectCurrentChapter);
  const newBeEvents = useAppSelector(selectProducerNewBeEvents);
  const dispatch = useAppDispatch();
  const params = useParams();
  let chapter_order_number = 0;
  let chapter_index = 0;

  let content = question.content;
  if (typeof question.content === "string") {
    content = JSON.parse(question.content);
    if (typeof content === "string") {
      content = JSON.parse(content);
      if (typeof content === "string") {
        content = JSON.parse(content);
      }
    }
  }

  useEffect(() => {
    if (currentChapter !== question.chapter_id) {
      dispatch(setCurrentChapter(question.chapter_id));
    }
  }, []);

  useEffect(() => {
    const newSideStatus: Record<string, any> = {};
    question.possible_answers
      .filter(
        (a) =>
          a.possible_answer_id &&
          sideTextStatus[a.possible_answer_id] === undefined &&
          a.possible_answer_id
      )
      .forEach((a) => {
        if (a.possible_answer_id) {
          newSideStatus[a.possible_answer_id] = false;
        }
      });

    setSideTextStatus({ ...sideTextStatus, ...newSideStatus });
    correctOrderNumberIfNeeded();
  }, [question.possible_answers]);

  const correctOrderNumberIfNeeded = () => {
    const answers = [...question.possible_answers];
    const sortedAnswers = answers.sort((a, b) => {
      if(a.order_number > b.order_number) {
        return 1;
      } else {
        return -1;
      }
    });
    sortedAnswers.forEach((pa, index) => {
      if(pa.order_number !== (index + 1)) {
        dispatch(
          changePossibleAnswerOrderNumber({
            orderNumber: index + 1,
            answerId: pa.possible_answer_id,
            frontAnswerId: pa.frontAnswerId || "",
            oldOrderNumber: pa.order_number,
          })
        );
      }
    })
  }

  currentModule?.chapters.forEach((c, i) => {
    if (c.chapter_id === question.chapter_id) {
      chapter_order_number = c.order_number;
      chapter_index = i;
    }
  });

  const [currentElement, setCurrentElement] = useState<{
    chapter: number;
    question: number;
    answer?: number;
  }>({
    chapter: chapter_index,
    question: question.question_index,
    answer: undefined,
  });

  useEffect(() => {
    let isVisible = false;
    let currentElemet = "";
    Object.keys(sideTextStatus).forEach((key) => {
      if (sideTextStatus[key]) currentElemet = key;
      isVisible = isVisible || sideTextStatus[key];
    });

    setSideTextVisibitlity(isVisible);

    const currentElementIndex = question.possible_answers.findIndex(
      (a) => a.possible_answer_id === currentElemet
    );

    if (currentElementIndex !== -1) {
      setCurrentElement({ ...currentElement, answer: currentElementIndex });

      setGuide(
        refreshedQuestion?.possible_answers[currentElementIndex].guidances
      );

      setExample(
        refreshedQuestion?.possible_answers[currentElementIndex].examples
      );
    } else {
      setGuide(refreshedQuestion?.guidances);

      setExample(refreshedQuestion?.examples);
    }
  }, [
    sideTextStatus,
    question,
    refreshedQuestion,
    refreshedQuestion?.possible_answers,
  ]);

  /* 
     @function onQuestionButtonClick 
     handle help button click
     @param id {string} - id of buttonClicked
     @param guide {string} - guide of button
     @param example {string} - example of button
   */
  const onQuestionButtonClick = (id: string, guide: any, example: any) => {
    const newSideText = { ...sideTextStatus };
    Object.keys(sideTextStatus).forEach((v) => {
      if (id === v) {
        newSideText[v] = !sideTextStatus[v];
        if (newSideText[v]) {
          setExample(example);
          setGuide(guide);
        }
      } else {
        newSideText[v] = false;
      }
    });

    setSideTextStatus({ ...newSideText });
  };

  /* 
    @function getAnswerObjectSimilarToSnapshot 
    returns Answer object with the keys that are consistent with the snapshot that is saved
    @param answer {Object} - Answer Object
  */
  const getAnswerObjectSimilarToSnapshot = (answer: any) => {
    const answerCopy = { ...answer };
    // making content key's value stringified to make it similar to snapshot object
    if (typeof answer.content === "string") {
      answerCopy.content = JSON.parse(answerCopy.content);
    }
    return answerCopy;
  };

  const answersList = question.possible_answers
    .slice()
    .sort((p, n) => p.order_number - n.order_number);

  const answers = answersList.map((a, i) => {
    return (
      <div key={i} className="flex flex-col">
        {showAddAnswer && i == 0 && (
          <button
            onClick={() => {
              onAddAnswer(a.order_number);
            }}
            className="text-left m-2 font-bold text-gray-400 text-2xl"
          >
            +
          </button>
        )}
        <div className="flex justify-between border-2 rounded-lg border-gray-400 p-4">
          <QuestionButton
            isOn={sideTextStatus[a.possible_answer_id || ""]}
            onClick={() =>
              onQuestionButtonClick(
                a.possible_answer_id || "",
                a.guidances ? a.guidances : [[]],
                a.examples ? a.examples : [[]]
              )
            }
            isPopulated={a.guidances.length > 0 || a.examples.length > 0}
          />
          <div className="flex flex-col">
            <ElementLabel
              chapter={chapter_order_number}
              question={question.order_number}
              answer={a.order_number}
            />
            <input
              //* rounded-full and same width and height not making the checkbox as circle. Look into it later */
              className="w-4 h-4 rounded-full"
              type="checkbox"
              checked={a.single}
              onChange={(e) => {
                dispatch(
                  setAnswerValue({
                    value: e.target.checked,
                    valueName: "single",
                    answerId: a.possible_answer_id,
                  })
                );
              }}
            />
          </div>
          <CompleteEditor
            element={{
              chapter: chapter_index,
              question: index,
              answer: question.possible_answers.indexOf(a),
            }}
            text={
              typeof a.content === "string"
                ? {
                    type: "doc",
                    content: RichTextTranslator({
                      text: JSON.parse(a.content).text,
                    }),
                  }
                : a.content
            }
            className="text-2xl border-b-2 border-gray-400 text-left"
            style={{ borderColor: color4 }}
            onUpdate={(val) => {
              onAnswerChange(val, a.possible_answer_id, a.frontAnswerId);
            }}
            elementAskingRerender={[
              a.order_number,
              a.possible_answer_id,
              a.frontAnswerId !== undefined ? a.frontAnswerId : "",
            ]}
            menuBarSiblings={
              <>
                {question.multiple && (
                  <div className="p-1">
                    <label>single</label>
                    <input
                      type="checkbox"
                      checked={a.single}
                      onChange={(e) => {
                        dispatch(
                          setAnswerValue({
                            value: e.target.checked,
                            valueName: "single",
                            answerId: a.possible_answer_id,
                            frontAnswerId: a.frontAnswerId,
                          })
                        );
                      }}
                    />
                  </div>
                )}
                {(a.comment === undefined || a.comment === null) && (
                  <Button
                    className="px-4 m-1 text-xs"
                    label="ADD COMMENT"
                    onClick={() => {
                      dispatch(
                        addComment({
                          chapterIndex: question.chapter_index,
                          questionIndex: question.question_index,
                          answerId:
                            a.possible_answer_id !== undefined
                              ? a.possible_answer_id
                              : a.frontAnswerId,
                        })
                      );
                    }}
                  />
                )}
                {a.frontAnswerId && newBeEvents[a.frontAnswerId] && (
                  <Button
                    label="SAVE"
                    className="px-4 m-1 text-xs"
                    onClick={() =>
                      dispatch(
                        addPossibleAnswerToBackend({
                          orgId: params.orgId || "",
                          frontAnswerId: a.frontAnswerId || "",
                          order_number: a.order_number,
                          content: a.content,
                          question_id: question.question_id,
                          single: a.single,
                          newBeEvents: newBeEvents,
                          comment: a.comment,
                        })
                      )
                    }
                  />
                )}
                {a.possible_answer_id &&
                  newBeEvents[a.possible_answer_id] &&
                  (newBeEvents[a.possible_answer_id].snapshot !==
                    JSON.stringify(getAnswerObjectSimilarToSnapshot(a)) ||
                    newBeEvents[a.possible_answer_id].elementModified) && (
                    <Button
                      label="SAVE"
                      className="px-4 m-1 text-xs"
                      onClick={() =>
                        dispatch(
                          changePossibleAnswerToBackend({
                            orgId: params.orgId || "",
                            possibleAnswerId: a.possible_answer_id || "",
                            order_number: a.order_number,
                            content: a.content,
                            questionId: question.question_id || "",
                            single: a.single,
                            guidances: a.guidances,
                            examples: a.examples,
                            newBeEvents: newBeEvents,
                            comment: a.comment,
                          })
                        )
                      }
                    />
                  )}
              </>
            }
          />
          <input
            className="w-12 h-12"
            type="number"
            value={a.order_number}
            min="1"
            max={answersList.length}
            onChange={(e) => {
              dispatch(
                changePossibleAnswerOrderNumber({
                  orderNumber: parseInt(e.target.value, 10),
                  answerId: a.possible_answer_id,
                  frontAnswerId: a.frontAnswerId || "",
                  oldOrderNumber: a.order_number,
                })
              );
            }}
          />
          {a.comment !== undefined && a.comment !== null && (
            <textarea
              onChange={debounce(
                (val) =>
                  dispatch(
                    changeComment({
                      comment: val.target.value,
                      chapterIndex: question.chapter_index,
                      questionIndex: question.question_index,
                      answerId:
                        a.possible_answer_id !== undefined
                          ? a.possible_answer_id
                          : a.frontAnswerId,
                    })
                  ),
                200
              )}
              defaultValue={a.comment}
            />
          )}
          <button
            className=""
            onClick={() => {
              dispatch(
                findConditionToChange({
                  chapter: question.chapter_id,
                  question: question.question_id,
                  answer: a.possible_answer_id,
                })
              );
              dispatch(
                askToDelete({
                  element: "answer",
                  possibleAnswerId: a.possible_answer_id,
                  frontAnswerId: a.frontAnswerId || "",
                })
              );
            }}
          >
            <FontAwesomeIcon icon={faMinus} size="2x" />
          </button>
        </div>
        {showAddAnswer && (
          <button
            onClick={() => {
              onAddAnswer(a.order_number + 1);
            }}
            className="text-left m-2 font-bold text-gray-400 text-2xl"
          >
            +
          </button>
        )}
      </div>
    );
  });

  return (
    <div className="w-full h-full relative">
      {sideTextVisibility && (
        <motion.div
          className={"absolute top-44 left-24  w-44  lg:w-96 z-50 "}
          initial={{ x: "-100vw" }}
          animate={{ x: 10 }}
          transition={{ type: "spring", damping: 30 }}
        >
          <MockGuideExample
            element={currentElement}
            guide={guide}
            example={example}
            currentElement={
              Object.keys(sideTextStatus).filter((k) => sideTextStatus[k])[0]
            }
            onGuideExampleChange={(type, val, currentElement, id) => {
              const element =
                currentElement !== "title"
                  ? question.possible_answers.findIndex(
                      (a) => a.possible_answer_id === currentElement
                    )
                  : currentElement;
              onGuideExampleChange(type, val, element, element === "title", id);
            }}
            addGuidesExampels={(id, currentElement) => {
              const possibleAnswerIndex = question.possible_answers.findIndex(
                (a) => a.possible_answer_id === currentElement
              );

              dispatch(
                addGuidesExamples({
                  id: id === "guide" ? "guidances" : "examples",
                  elementIndex: possibleAnswerIndex,
                  isQuestion: currentElement === "title",
                  chapterId: question.chapter_index,
                  questionId: question.question_index,
                })
              );
            }}
            onDelete={(id, type, isInBackend, currentElement) => {
              const current =
                currentElement === "title"
                  ? question.question_id
                  : currentElement;
              if (type === "guide") {
                dispatch(
                  removeGuidance({
                    id: id,
                    isInBackend: isInBackend,
                    current,

                    orgId: params.orgId || "",
                  })
                );
              } else {
                dispatch(
                  removeExamples({
                    id: id,
                    isInBackend: isInBackend,
                    current,

                    orgId: params.orgId || "",
                  })
                );
              }
            }}
            onClose={() => {
              setSideTextVisibitlity(false);
              onQuestionButtonClick(
                Object.keys(sideTextStatus).filter((k) => sideTextStatus[k])[0],
                guide,
                example
              );
            }}
          />
        </motion.div>
      )}
      <div className="m-auto  h-fit w-2/5 flex flex-col gap-y-4 relative">
        <StepCondition
          step={question.question_index}
          chapter={question.chapter_index}
        />
        <div className="flex justify-between border-2 rounded-xl border-gray-400 p-4">
          <QuestionButton
            isOn={sideTextStatus["title"]}
            onClick={() =>
              onQuestionButtonClick(
                "title",
                question.guidances ? question.guidances : [[]],
                question.examples ? question.examples : [[]]
              )
            }
            isPopulated={
              question.guidances.length > 0 || question.examples.length > 0
            }
          />
          <ElementLabel
            chapter={chapter_order_number}
            question={question.order_number}
            conclusion={question.conclusion}
          />
          <CompleteEditor
            element={{ chapter: chapter_index, question: index }}
            text={
              typeof content !== "string" && "text" in content
                ? {
                    type: "doc",
                    content: RichTextTranslator({
                      text: content.text,
                    }),
                  }
                : content
            }
            className="text-2xl border-b-2 border-gray-400 text-left"
            // style={{ borderColor: style.color4 }}
            onUpdate={(val) =>
              onQuestionChange(index, val, question.question_id)
            }
            menuBarSiblings={
              <>
                {question.conclusion && (
                  <div className="p-1">
                    <label>hidden</label>
                    <input
                      type="checkbox"
                      checked={question.should_be_hidden}
                      onChange={(e) => {
                        dispatch(
                          setQuestionValue({
                            value: e.target.checked,
                            valueName: "should_be_hidden",
                            questionId: question.question_id,
                          })
                        );
                      }}
                    />
                  </div>
                )}
                <div className="p-1">
                  <label>conclusion</label>
                  <input
                    type="checkbox"
                    checked={question.conclusion}
                    onChange={(e) => {
                      dispatch(
                        setQuestionValue({
                          value: e.target.checked,
                          valueName: "conclusion",
                          questionId: question.question_id,
                        })
                      );
                    }}
                  />
                </div>
                {!question.conclusion && (
                  <div className="p-1">
                    <label>multiple</label>
                    <input
                      type="checkbox"
                      checked={question.multiple}
                      onChange={(e) => {
                        dispatch(
                          setQuestionValue({
                            value: e.target.checked,
                            valueName: "multiple",
                            questionId: question.question_id,
                          })
                        );
                      }}
                    />
                  </div>
                )}
                {question.conclusion && (
                  <div className="p-1">
                    <label>print</label>
                    <input
                      type="checkbox"
                      checked={question.print}
                      onChange={(e) => {
                        dispatch(
                          setQuestionValue({
                            value: e.target.checked,
                            valueName: "print",
                            questionId: question.question_id,
                          })
                        );
                      }}
                    />
                  </div>
                )}
                {(question.comment === undefined ||
                  question.comment === null) && (
                  <Button
                    label="ADD COMMENT"
                    className="px-4 m-1 text-xs"
                    onClick={() => {
                      dispatch(
                        addComment({
                          chapterIndex: question.chapter_index,
                          questionIndex: question.question_index,
                        })
                      );
                    }}
                  />
                )}
                {question.question_id &&
                  newBeEvents[question.question_id] &&
                  (newBeEvents[question.question_id].snapshot !==
                    JSON.stringify(createQuestionSnapshot(question)) ||
                    newBeEvents[question.question_id].elementModified) && (
                    <Button
                      className="px-4 m-1 text-xs"
                      onClick={() => {
                        dispatch(
                          changeQuestionInBackend({
                            orgId: params.orgId || "",
                            questionId: question.question_id || "",
                            order_number: question.order_number,
                            content: question.content,
                            chapterId: question.chapter_id,
                            conclusion: question.conclusion,
                            displayId: question.display_index,
                            multiple: question.multiple,
                            print: question.print,
                            should_be_hidden: question.should_be_hidden,
                            trigger: question.trigger,
                            guidances: refreshedQuestion?.guidances || [],
                            examples: refreshedQuestion?.examples || [],
                            newBeEvents: newBeEvents,
                          })
                        );
                      }}
                      label="SAVE"
                    />
                  )}
              </>
            }
          />
          {question.comment !== undefined && question.comment !== null && (
            <textarea
              onChange={debounce(
                (val) =>
                  dispatch(
                    changeComment({
                      comment: val.target.value,
                      chapterIndex: question.chapter_index,
                      questionIndex: question.question_index,
                    })
                  ),
                200
              )}
              defaultValue={question.comment}
            />
          )}
        </div>
        {!question.conclusion && (
          <>
            <div className="w-full ">
              <h1 className="text-2xl text-left">Answers</h1>
            </div>

            {answers}
            {showAddAnswer && answers.length === 0 && (
              <button
                onClick={() => onAddAnswer(1)}
                className="text-left m-2 font-bold text-gray-400 text-2xl"
              >
                +
              </button>
            )}
            <button onClick={() => {
              setShowAddAnswer(!showAddAnswer);
              if(showAddAnswer) {
                dispatch(removeFrontAnswers({chapterIndex: chapter_order_number, questionIndex: question.order_number}))
              }
            }}>
              {showAddAnswer ? "Cancel Add Answer" : "Add Answer"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/*
  @function generateSideTextFromAnswers
  generate sideText help imporfation from answers
  @params answers {array} - given answers
  @params title  {object} - guide and example for title
  @returns {object} - sideText object
 */
function generateSideTextFromAnswers(answers: any) {
  const result: Record<string, any> = {};
  answers.forEach((answer: any) => (result[answer.possible_answer_id] = false));
  result["title"] = false;
  return result;
}

function MockGuideExample({
  guide = [],
  example = [],
  currentElement,
  onGuideExampleChange,
  addGuidesExampels,
  onDelete,
  onClose,
  element,
}: {
  guide: GuidanceOrExample[];
  example: GuidanceOrExample[];
  currentElement: string;
  onGuideExampleChange: (
    type: "guidances" | "examples",
    val: JSONContent,
    currentElement: string,
    id: string
  ) => void;
  addGuidesExampels: (id: string, element: string) => void;
  onDelete: (
    id: string,
    anotherId: string,
    isInBackend: boolean,
    element: string
  ) => void;
  onClose: () => void;
  element: {
    [key: string]: unknown;
  };
}) {
  const [state, setState] = useState({
    id: "guide",
    el: guide,
    currentElement: currentElement ? currentElement : "",
  });

  useEffect(() => {
    setState({ ...state, currentElement: currentElement });
  }, [currentElement]);

  useEffect(() => {
    if (state.id === "guide") {
      setState({ ...state, el: guide });
    }

    if (state.id === "example") {
      setState({ ...state, el: example });
    }
  }, [guide, example]);

  return (
    <div className="guidesAndExamples">
      <GuideButtons
        elements={[
          { label: "GUIDES", id: "guide", current: state.id === "guide" },
          { label: "EXAMPLES", id: "example", current: state.id === "example" },
        ]}
        onClick={(id) => {
          setState(() => {
            if (id === "guide") {
              return { id: id, el: guide, currentElement: currentElement };
            }
            return { id: id, el: example, currentElement: currentElement };
          });
        }}
        onClose={onClose}
      />
      <div className=" flex flex-col">
        {state.el.map((e, i) => {
          let text = e.content;
          if (typeof text === "string") {
            text = JSON.parse(text);
          }

          if (text === null) {
            text = { text: [] };
          }

          return (
            <div className="flex" key={i}>
              <div className="border border-2 border-black my-4 w-full">
                <CompleteEditor
                  element={{ ...element, [state.id]: i }}
                  text={
                    Array.isArray(text.text)
                      ? {
                          type: "doc",
                          content:
                            RichTextTranslator({ text: text.text }).length === 0
                              ? [{ type: "text", text: "" }]
                              : RichTextTranslator({ text: text.text }),
                        }
                      : text
                  }
                  onUpdate={(val) => {
                    onGuideExampleChange(
                      state.id === "guide" ? "guidances" : "examples",
                      val,
                      currentElement,
                      state.id === "guide"
                        ? e.guidance_id
                          ? e.guidance_id
                          : e.frontId
                        : e.example_id
                        ? e.example_id
                        : e.frontId
                    );
                  }}
                  elementAskingRerender={[
                    state.currentElement,
                    state.id,
                    e.example_id,
                    e.guidance_id,
                  ]}
                />
              </div>
              <button
                onClick={() =>
                  onDelete(
                    state.id === "guide"
                      ? e.guidance_id
                        ? e.guidance_id
                        : e.frontId
                      : e.example_id
                      ? e.example_id
                      : e.frontId,
                    state.id,
                    !!e.guidance_id || !!e.example_id,
                    currentElement
                  )
                }
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
            </div>
          );
        })}
        <Button
          label="ADD ELEMENT"
          className="m-auto"
          onClick={() => addGuidesExampels(state.id, currentElement)}
        />
      </div>
    </div>
  );
}

/*
    
*/

//<RichTextReader text={state === "guide" ? guide : example} />
